import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { styled } from "@mui/material/styles";
import { Alert, LinearProgress, Box, Skeleton, Stack } from "@mui/material";
import { fetchIndicatorApi } from "../../../../Utilities/Url";
import { TickerName, isLoading } from "../../../../Redux/Action";
import { utils, writeFile } from "xlsx"; // Import xlsx library
import DownloadIcon from "@mui/icons-material/Download";
import { canExportToCSV } from "../config";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const IndicatorTable = () => {
  const [sortColumn, setSortColumn] = useState("symbols");
  const [sortDirection, setSortDirection] = useState("asc");
  const rowsPerPage = useSelector((state) => state.RowsPerPage);
  const page = useSelector((state) => state.SetPage);
  const searchItem = useSelector((state) => state.SearchItems);
  const setError = useSelector((state) => state.SetError);
  const SelectedDropdownOption = useSelector(
    (state) => state.SelectedDropdownOption
  );
  const setActiveButton = useSelector((state) => state.ActiveButton);
  const selectedExchange = useSelector((state) => state.SetExchange);
  const tableTickerName = useSelector((state) => state.TickerName);
  const isLoading = useSelector((state) => state.isLoading);
  const authToken = useSelector((state) => state.authToken);
  const selectedAnalytics = useSelector((state) => state.selectedAnalytics);
  const dispatch = useDispatch();

  useEffect(() => {
    if (SelectedDropdownOption === "Indicator") {
      fetchIndicatorApi(
        selectedExchange,
        setActiveButton,
        SelectedDropdownOption,
        dispatch,
        authToken,
        selectedAnalytics
      );
    }
  }, [
    selectedExchange,
    setActiveButton,
    SelectedDropdownOption,
    dispatch,
    selectedAnalytics,
  ]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "4px 8px",
    },
    textAlign: "right",
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      // backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  }));

  const rowsPerPageOptions = [5, 10, 25];

  const handleChangePage = (event, newPage) => {
    dispatch({ type: "SET_PAGE", payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: "ROWS_PER_PAGE",
      payload: parseInt(event.target.value, 10),
    });
    dispatch({ type: "SET_PAGE", payload: 0 });
  };

  const sortIndicators = (indicators) => {
    const order = ["ADX", "BollingerBands", "MACD", "Rsi", "Stoch", "StochRsi"];
    return indicators.sort(
      (a, b) => order.indexOf(a.Indicator) - order.indexOf(b.Indicator)
    );
  };

  const handleFirstPageButtonClick = (event) => {
    handleChangePage(event, 0);
  };

  const handleLastPageButtonClick = (event, count) => {
    handleChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;

    return (
      <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
        <IconButton
          onClick={(event) => onPageChange(event, 0)}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page - 1)}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={(event) => handleLastPageButtonClick(event, count)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </div>
    );
  };
  const handleApiStructure = () => {};
  const handleExportCSV = () => {
    // Create a header row for the CSV file
    const headers = [
      "Ticker",
      "ADX",
      "B.BANDS",
      "MACD",
      "RSI",
      "STOCH",
      "STOCHRSI",
      "STRENGTH",
      "OVERALL",
    ];

    // Map data to rows for the CSV file

    const csvData = (
      tableTickerName &&
      (tableTickerName[0] || tableTickerName)
    ).map((item, index) => {
      // Check if IndicatorResults is valid
      const getIndicatorValue = (indicator) =>
        item.IndicatorResults && Array.isArray(item.IndicatorResults)
          ? item.IndicatorResults.find(
              (ind) => ind.Indicator === indicator
            )?.Value?.toFixed(2) || ""
          : "";

      const adxValue = getIndicatorValue("ADX");
      const bbandsValue = getIndicatorValue("BollingerBands");
      const macdValue = getIndicatorValue("MACD");
      const rsiValue = getIndicatorValue("Rsi");
      const stochValue = getIndicatorValue("Stoch");
      const stochRsiValue = getIndicatorValue("StochRsi");

      return [
        item.Ticker || "",
        adxValue,
        bbandsValue,
        macdValue,
        rsiValue,
        stochValue,
        stochRsiValue,
        (item.IndicatorSummary?.SummaryPercent || "") + "%",
        (item.Overall?.SummaryPercent || "") + "%",
      ];
    });
    const data = [headers, ...csvData];
    const ws = utils.aoa_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "indicatorData");
    writeFile(wb, "indicatorData.xlsx");
  };

  let filterTicker = (
    tableTickerName &&
    (tableTickerName[0] || tableTickerName)
  ).filter((ele) => ele.Ticker.includes(searchItem[searchItem.length - 1]));
  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return setError === null ? (
    <div>
      {isLoading ? (
        <Stack spacing={1} padding={"5px"}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
          />
          <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
          </div>
        </Stack>
      ) : (
        <TableContainer sx={{ maxHeight: "100vh" }}>
          {canExportToCSV && (
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                padding: "6px",
                cursor: "pointer",
              }}
            >
              <DownloadIcon
                onClick={handleExportCSV}
                aria-label="export to CSV"
              />
            </div>
          )}
          <Table
            stickyHeader
            aria-label="customized table"
            size="small"
            className="table"
          >
            <TableHead>
              <TableRow sx={{ bottom: -20 }}></TableRow>
              <TableRow>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                    maxWidth: "40px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("symbols")}
                  >
                    SYMBOLS
                    {sortColumn === "symbols" && sortDirection === "asc" ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <KeyboardArrowUpIcon />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  ADX
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  B.BANDS
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  MACD
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  RSI
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  STOCH
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  STOCHRSI
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  STRENGTH
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      maxWidth: "40px",
                    }}
                    onClick={() => handleSort("overall")}
                  >
                    OVERALL
                    {sortColumn === "overall" && sortDirection === "asc" ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <KeyboardArrowUpIcon />
                    )}
                  </div>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchItem.length !== 0 && filterTicker.length === 0 ? (
                <TableRow>
                  <StyledTableCell
                    colSpan={10}
                    align="center"
                    sx={{ textAlign: "center" }}
                  >
                    NO DATA FOUND
                  </StyledTableCell>
                </TableRow>
              ) : (
                (filterTicker.length === 0
                  ? tableTickerName && (tableTickerName[0] || tableTickerName)
                  : filterTicker
                )
                  ?.map((item) => ({
                    ...item,
                    IndicatorResults: sortIndicators(item.IndicatorResults),
                  }))
                  .sort((a, b) => {
                    if (sortColumn === "symbols") {
                      return sortDirection === "asc"
                        ? a.Ticker.localeCompare(b.Ticker)
                        : b.Ticker.localeCompare(a.Ticker);
                    } else if (sortColumn === "overall") {
                      const comparison =
                        b.Overall.SummaryPercent - a.Overall.SummaryPercent;
                      return sortDirection === "asc" ? comparison : -comparison;
                    }
                    return 0;
                  })
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell style={{ textAlign: "left" }}>
                        {item.Ticker}
                      </StyledTableCell>
                      {item.IndicatorResults.map((indicator, i) => (
                        <StyledTableCell key={i} sx={{ textAlign: "right" }}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginLeft: "15px",
                            }}
                          >
                            <div
                              style={{
                                height: "10px",
                                width: "10px",
                                backgroundColor: getColor(indicator.Indication),
                                borderRadius: "100%",
                              }}
                            ></div>
                            {indicator.Value?.toFixed(2)}
                          </div>
                        </StyledTableCell>
                      ))}
                      <StyledTableCell sx={{ textAlign: "right" }}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginLeft: "15px",
                          }}
                        >
                          <div
                            style={{
                              height: "10px",
                              width: "10px",
                              backgroundColor: getColor(
                                item.IndicatorSummary.Summary
                              ),
                              borderRadius: "100%",
                            }}
                          ></div>
                          {item.IndicatorSummary.SummaryPercent?.toFixed(2)}%
                        </div>
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "right" }}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginLeft: "15px",
                          }}
                        >
                          <div
                            style={{
                              height: "10px",
                              width: "10px",
                              backgroundColor: getColor(item.Overall.Summary),
                              borderRadius: "100%",
                            }}
                          ></div>
                          {item.Overall.SummaryPercent?.toFixed(2)}%
                        </div>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!(searchItem.length !== 0 && filterTicker.length === 0) &&
        !isLoading && (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={
              filterTicker.length === 0
                ? (tableTickerName && (tableTickerName[0] || tableTickerName))
                    .length
                : filterTicker.length || 0
            }
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        )}
    </div>
  ) : (
    <Alert severity="error">{setError}</Alert>
  );
};

export default IndicatorTable;

function getColor(indication) {
  switch (indication) {
    case "StrongBuy":
      return "#43a047";
    case "Buy":
      return "#81c784";
    case "OverBought":
      return "#1b5e20";
    case "Sell":
      return "#ef5350";
    case "StrongSell":
      return "#d32f2f";
    case "OverSold":
      return "#b71c1c";
    case "Neutral":
      return "#cfd8dc";
    default:
      return "#000000";
  }
}
